import { pushNotificationTypes, planConsumption, type PushNotificationType } from './adminNotifications';

// ─── Tipos normalizados ──────────────────────────────────

export type NotificationStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed';
export type NotificationChannel = 'push' | 'email' | 'whatsapp' | 'in_app';
export type NotificationAudience = 'todos' | 'oyentes_activos' | 'seguidores' | 'vip';
export type NotificationModule = 'events' | 'contests' | 'live' | 'programs' | 'posts' | 'manual';

export interface UnifiedNotification {
  id: number;
  type: PushNotificationType;
  title: string;
  message: string;
  status: NotificationStatus;
  channel: NotificationChannel;
  audience: NotificationAudience;
  language: string;
  relatedModule: NotificationModule;
  relatedItemId?: number;
  relatedItemName?: string;
  sentAt: string | null;
  scheduledFor: string | null;
  recipients: number;
  delivered: number;
  opened: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Labels ──────────────────────────────────────────────

export const notificationStatusLabels: Record<NotificationStatus, string> = {
  draft: 'Borrador',
  scheduled: 'Programado',
  sending: 'Enviando',
  sent: 'Enviado',
  cancelled: 'Cancelado',
  failed: 'Fallido',
};

export const notificationStatusColors: Record<NotificationStatus, { bg: string; text: string; dot: string }> = {
  draft: { bg: 'bg-foreground-100', text: 'text-foreground-500', dot: 'bg-foreground-400' },
  scheduled: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  sending: { bg: 'bg-sky-100', text: 'text-sky-700', dot: 'bg-sky-500' },
  sent: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-500', dot: 'bg-red-400' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-600' },
};

export const channelLabels: Record<NotificationChannel, string> = {
  push: 'Push',
  email: 'Email',
  whatsapp: 'WhatsApp',
  in_app: 'In-App',
};

export const channelIcons: Record<NotificationChannel, string> = {
  push: 'ri-smartphone-line',
  email: 'ri-mail-line',
  whatsapp: 'ri-whatsapp-line',
  in_app: 'ri-notification-3-line',
};

export const channelMockBadge: Record<NotificationChannel, string> = {
  push: '',
  email: 'Simulado',
  whatsapp: 'Simulado',
  in_app: 'Simulado',
};

export const audienceLabels: Record<NotificationAudience, string> = {
  todos: 'Todos los oyentes',
  oyentes_activos: 'Oyentes activos (7 días)',
  seguidores: 'Seguidores de la radio',
  vip: 'VIP / Premium',
};

export const audienceCounts: Record<NotificationAudience, number> = {
  todos: 1247,
  oyentes_activos: 842,
  seguidores: 623,
  vip: 156,
};

export const moduleLabels: Record<NotificationModule, string> = {
  events: 'Eventos',
  contests: 'Concursos',
  live: 'Lives',
  programs: 'Programas',
  posts: 'Publicaciones',
  manual: 'Manual',
};

export const moduleIcons: Record<NotificationModule, string> = {
  events: 'ri-calendar-event-line',
  contests: 'ri-trophy-line',
  live: 'ri-live-line',
  programs: 'ri-mic-line',
  posts: 'ri-file-list-3-line',
  manual: 'ri-notification-3-line',
};

export const moduleColors: Record<NotificationModule, { bg: string; text: string }> = {
  events: { bg: 'bg-rose-100', text: 'text-rose-700' },
  contests: { bg: 'bg-amber-100', text: 'text-amber-700' },
  live: { bg: 'bg-red-100', text: 'text-red-700' },
  programs: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  posts: { bg: 'bg-sky-100', text: 'text-sky-700' },
  manual: { bg: 'bg-foreground-100', text: 'text-foreground-500' },
};

// ─── Engine ──────────────────────────────────────────────

const now = new Date();
const hoursAgo = (h: number) => new Date(now.getTime() - h * 3600000).toISOString();
const hoursFromNow = (h: number) => new Date(now.getTime() + h * 3600000).toISOString();

let nextId = 100;

const seedNotifications: UnifiedNotification[] = [
  {
    id: 1, type: 'transmision', title: '¡Estamos al aire!',
    message: 'RadioFrecuenciaWeb inició transmisión. Conectate ahora y disfrutá de la mejor música en vivo. ¡No te lo pierdas!',
    status: 'sent', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'manual', sentAt: hoursAgo(2), scheduledFor: null,
    recipients: 1247, delivered: 1203, opened: 487,
    createdAt: hoursAgo(3), updatedAt: hoursAgo(2),
  },
  {
    id: 2, type: 'programa', title: 'Arranca El Mañanero',
    message: 'Valentina Ríos inicia El Mañanero con las mejores noticias, entrevistas y la música que te despierta. Sintonizá ahora.',
    status: 'sent', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'programs', relatedItemId: 1, relatedItemName: 'El Mañanero',
    sentAt: hoursAgo(6), scheduledFor: null,
    recipients: 1247, delivered: 1189, opened: 523,
    createdAt: hoursAgo(7), updatedAt: hoursAgo(6),
  },
  {
    id: 3, type: 'evento', title: 'Maratón Musical 80s esta noche',
    message: 'Preparate para 4 horas de los mejores clásicos de los 80. Hoy a las 20:00. ¡Invitá a tus amigos y armá la previa!',
    status: 'sent', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'events', relatedItemId: 1, relatedItemName: 'Maratón Musical 80s',
    sentAt: hoursAgo(24), scheduledFor: null,
    recipients: 1247, delivered: 1156, opened: 612,
    createdAt: hoursAgo(25), updatedAt: hoursAgo(24),
  },
  {
    id: 4, type: 'concurso', title: 'Ganá entradas VIP — Último día',
    message: 'Hoy cierran las participaciones para ganar entradas VIP al concierto. Solo necesitás estar escuchando y mandar tu palabra clave.',
    status: 'sent', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'contests', relatedItemId: 1, relatedItemName: 'Gana Meet & Greet con Los Ecos',
    sentAt: hoursAgo(30), scheduledFor: null,
    recipients: 1247, delivered: 1140, opened: 701,
    createdAt: hoursAgo(31), updatedAt: hoursAgo(30),
  },
  {
    id: 5, type: 'promocion', title: '2x1 en la tienda oficial',
    message: 'Solo por hoy: llevate 2 productos de la tienda oficial por el precio de 1. Usá el código RADIO2X1 al finalizar tu compra.',
    status: 'sent', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'manual', sentAt: hoursAgo(48), scheduledFor: null,
    recipients: 1247, delivered: 1123, opened: 398,
    createdAt: hoursAgo(49), updatedAt: hoursAgo(48),
  },
  {
    id: 6, type: 'programa', title: 'Zona Deportiva en 30 minutos',
    message: 'Roberto Aguilar trae todo el análisis deportivo. Fútbol nacional, internacional y la previa del partido de esta noche.',
    status: 'scheduled', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'programs', relatedItemId: 3, relatedItemName: 'Zona Deportiva',
    scheduledFor: hoursFromNow(2), sentAt: null,
    recipients: 1247, delivered: 0, opened: 0,
    createdAt: hoursAgo(1), updatedAt: hoursAgo(1),
  },
  {
    id: 7, type: 'locutor', title: 'Andrés Castillo en vivo',
    message: 'Andrés Castillo está al aire con La Hora Pico. Humor, actualidad y la mejor compañía para tu tarde. ¡Conectate ya!',
    status: 'scheduled', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'manual', scheduledFor: hoursFromNow(5), sentAt: null,
    recipients: 1247, delivered: 0, opened: 0,
    createdAt: hoursAgo(2), updatedAt: hoursAgo(2),
  },
  {
    id: 8, type: 'patrocinador', title: 'Café del Sol — Descuento especial',
    message: 'Presentá esta notificación en Café del Sol y obtené un 15% de descuento en tu bebida favorita. Patrocinador oficial de RadioFrecuenciaWeb.',
    status: 'sent', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'manual', sentAt: hoursAgo(12), scheduledFor: null,
    recipients: 1247, delivered: 1198, opened: 445,
    createdAt: hoursAgo(13), updatedAt: hoursAgo(12),
  },
  {
    id: 9, type: 'transmision', title: '¡Volvimos al aire!',
    message: 'Transmisión restablecida. Disculpá la interrupción, ya estamos de vuelta con toda la energía. Gracias por tu paciencia.',
    status: 'sent', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'manual', sentAt: hoursAgo(15), scheduledFor: null,
    recipients: 1247, delivered: 1102, opened: 634,
    createdAt: hoursAgo(16), updatedAt: hoursAgo(15),
  },
  {
    id: 10, type: 'evento', title: '¡Fiesta Latina confirmada!',
    message: 'Acabamos de publicar la Fiesta Latina del próximo sábado. Música en vivo, comida y sorpresas. ¡Confirmá tu asistencia ya!',
    status: 'draft', channel: 'push', audience: 'todos', language: 'es',
    relatedModule: 'events', relatedItemId: 5, relatedItemName: 'Fiesta Latina',
    sentAt: null, scheduledFor: null,
    recipients: 0, delivered: 0, opened: 0,
    createdAt: hoursAgo(1), updatedAt: hoursAgo(1),
  },
];

// ─── Mutable state ───────────────────────────────────────

let notifications: UnifiedNotification[] = [...seedNotifications];
let listeners: Set<() => void> = new Set();

function notify() {
  listeners.forEach((fn) => fn());
}

// ─── Public API ──────────────────────────────────────────

export function getNotifications(): UnifiedNotification[] {
  return notifications;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function addNotification(partial: Omit<UnifiedNotification, 'id' | 'createdAt' | 'updatedAt'>): UnifiedNotification {
  const id = nextId++;
  const ts = new Date().toISOString();
  const notification: UnifiedNotification = {
    ...partial,
    id,
    createdAt: ts,
    updatedAt: ts,
  };
  notifications = [notification, ...notifications];
  notify();
  return notification;
}

export function updateNotification(id: number, updates: Partial<UnifiedNotification>): UnifiedNotification | null {
  const idx = notifications.findIndex((n) => n.id === id);
  if (idx === -1) return null;
  notifications = notifications.map((n) =>
    n.id === id ? { ...n, ...updates, updatedAt: new Date().toISOString() } : n
  );
  notify();
  return notifications.find((n) => n.id === id)!;
}

export function cancelNotification(id: number): UnifiedNotification | null {
  return updateNotification(id, { status: 'cancelled' });
}

export function duplicateNotification(id: number): UnifiedNotification | null {
  const original = notifications.find((n) => n.id === id);
  if (!original) return null;
  const duplicated = addNotification({
    type: original.type,
    title: `${original.title} (copia)`,
    message: original.message,
    status: 'draft',
    channel: original.channel,
    audience: original.audience,
    language: original.language,
    relatedModule: original.relatedModule,
    relatedItemId: original.relatedItemId,
    relatedItemName: original.relatedItemName,
    sentAt: null,
    scheduledFor: null,
    recipients: 0,
    delivered: 0,
    opened: 0,
  });
  return duplicated;
}

export function getPlanConsumption() {
  return { ...planConsumption };
}

export function consumeFromPlan(): boolean {
  if (planConsumption.remaining <= 0) return false;
  planConsumption.usedThisMonth += 1;
  planConsumption.remaining -= 1;
  notify();
  return true;
}

export function returnToPlan(): void {
  if (planConsumption.usedThisMonth <= 0) return;
  planConsumption.usedThisMonth -= 1;
  planConsumption.remaining += 1;
  notify();
}

export { pushNotificationTypes, type PushNotificationType } from './adminNotifications';
export type { PushNotificationTypeMeta } from './adminNotifications';